/**
 * The Early Access Agreement, version 1.0.
 *
 * Transcribed mechanically from lg-early-access-agreement.md rather than by
 * hand, so the words are the words. Markdown emphasis is dropped (it carries
 * nothing once rendered) and the signature blanks at the end are omitted —
 * the four form fields below the text are what collect those.
 *
 * Nothing else in the app reads the body, so replacing this constant is the
 * whole change when the text moves.
 *
 * If the text changes materially, bump BETA_AGREEMENT_VERSION here AND in
 * backend/src/jobs/service.ts — the backend refuses an acceptance whose version
 * is not the one it has in force, so the two must move together.
 */
export const BETA_AGREEMENT_VERSION = '1.0';

/**
 * A paragraph, or a bullet list. The agreement leans on bullets for the lists
 * of things the participant is responsible for, and rendering those as
 * paragraphs prefixed with "- " makes a legal document people are required to
 * read noticeably harder to read.
 */
export type AgreementBlock = string | { bullets: string[] };

export interface AgreementSection {
  heading: string;
  body: AgreementBlock[];
}

export const BETA_AGREEMENT_BODY: AgreementSection[] = [
  {
    heading: "EARLY ACCESS PROGRAM AGREEMENT",
    body: [
      "This Early Access Agreement (\"Agreement\") is between LienGuardian (\"we,\" \"us,\" \"our\") and the individual or entity accepting this Agreement (\"you,\" \"your,\" \"Participant\").",
      "By clicking \"I Accept\" or accessing the LienGuardian platform, you acknowledge that you have read, understood, and agree to be bound by this Agreement."
    ]
  },
  {
    heading: "1. EARLY ACCESS STATUS",
    body: [
      "LienGuardian is currently in an early access phase. The platform is under active development and has not been publicly released. You are being granted access for the mutual benefit of both parties — you receive early use of the platform, and we receive real-world feedback to improve it.",
      "You understand and agree that:",
      {
        bullets: [
          "The platform may contain bugs, errors, or incomplete features",
          "Features may change, break, or be removed without notice",
          "Service interruptions may occur without warning",
          "Data formats, APIs, and interfaces are not finalized and may change"
        ]
      }
    ]
  },
  {
    heading: "2. NOT A SUBSTITUTE FOR PROFESSIONAL JUDGMENT",
    body: [
      "LienGuardian is a compliance assistance tool. It is not a law firm, does not provide legal advice, and does not guarantee legal compliance.",
      "You are solely responsible for:",
      {
        bullets: [
          "Verifying the accuracy of all property data, owner information, legal descriptions, and parcel identifiers before relying on them",
          "Reviewing all generated documents — including but not limited to Notices of Commencement, Notices to Owner, lien waivers, claims of lien, and Contractor's Final Payment Affidavits — before signing, sending, or recording them",
          "Ensuring all documents are served within the deadlines required by Florida Statutes Chapter 713",
          "Confirming that all financial information, payment amounts, and waiver amounts are correct before execution",
          "Complying with all applicable federal, state, and local laws"
        ]
      },
      "The Contractor's Final Payment Affidavit generated by LienGuardian is a sworn document under Florida Statute 713.06(3)(d). By signing it, you are making a statement under oath. You are responsible for verifying its accuracy. LienGuardian does not verify, warrant, or guarantee the truth of any statements contained in documents you sign through the platform."
    ]
  },
  {
    heading: "3. PROPERTY DATA",
    body: [
      "LienGuardian retrieves property information from county Property Appraiser records and other public sources. This data may be:",
      {
        bullets: [
          "Incomplete, outdated, or inaccurate",
          "Delayed relative to the county's current records",
          "Missing fields that are material to your compliance obligations",
          "Sourced from automated retrieval processes that may malfunction"
        ]
      },
      "You must independently verify all property data before using it in any legal document or compliance action. Do not rely solely on LienGuardian's property data for determining legal ownership, parcel boundaries, or mailing addresses for service of process."
    ]
  },
  {
    heading: "4. CERTIFIED MAIL AND FINANCIAL TRANSACTIONS",
    body: [
      "Notices sent through LienGuardian are transmitted via certified mail through the United States Postal Service. Once a notice is sent, it cannot be recalled. The platform provides a brief cancellation window, but once that window closes, the mailing is irreversible.",
      "Wallet charges for certified mailings are real financial transactions. You authorize these charges when you initiate a mailing action. Charges for successfully dispatched mailings are non-refundable, including mailings sent during this early access period."
    ]
  },
  {
    heading: "5. DEADLINE CALCULATIONS",
    body: [
      "LienGuardian calculates compliance deadlines (including but not limited to Notice to Owner deadlines, lien filing deadlines, and lien enforcement deadlines) based on dates you provide and the platform's interpretation of Florida Statutes Chapter 713.",
      "These calculations are provided as a convenience and are not guaranteed to be accurate. Deadline calculations may be affected by:",
      {
        bullets: [
          "Incorrect dates entered by you",
          "Software bugs or errors in calculation logic",
          "Changes in applicable law",
          "County-specific rules or procedures not reflected in the platform",
          "Judicial interpretations that differ from the platform's logic"
        ]
      },
      "You are responsible for independently verifying all deadlines. A missed deadline due to reliance on LienGuardian's calculations is your responsibility, not ours."
    ]
  },
  {
    heading: "6. LIMITATION OF LIABILITY",
    body: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, LIEGUARDIAN'S TOTAL LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING OUT OF OR RELATED TO THIS AGREEMENT OR YOUR USE OF THE PLATFORM SHALL NOT EXCEED THE TOTAL AMOUNT YOU HAVE PAID TO LIEGUARDIAN IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.",
      "IN NO EVENT SHALL LIEGUARDIAN BE LIABLE FOR:",
      {
        bullets: [
          "Loss of lien rights, mechanic's lien priority, or any statutory remedy",
          "Missed filing or service deadlines",
          "Incorrect, incomplete, or outdated property data",
          "Documents signed or sent based on inaccurate information",
          "Damages arising from reliance on deadline calculations",
          "Any indirect, incidental, special, consequential, or punitive damages",
          "Lost profits, lost revenue, or loss of business opportunity"
        ]
      },
      "This limitation applies regardless of the theory of liability (contract, tort, negligence, strict liability, or otherwise) and even if LienGuardian has been advised of the possibility of such damages."
    ]
  },
  {
    heading: "7. INDEMNIFICATION",
    body: [
      "You agree to indemnify, defend, and hold harmless LienGuardian, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:",
      {
        bullets: [
          "Your use of the platform",
          "Documents you generate, sign, send, or record using the platform",
          "Your failure to verify the accuracy of data or documents",
          "Your failure to meet any legal deadline or compliance requirement",
          "Any breach of this Agreement by you"
        ]
      }
    ]
  },
  {
    heading: "8. DATA AND PRIVACY",
    body: [
      "During the early access period, we may collect usage data, error reports, and feedback to improve the platform. We will not sell your data or share it with third parties except as necessary to operate the platform (e.g., mail delivery services, payment processors).",
      "Your business data (job details, property information, financial records) is stored securely and is accessible only to your authorized users. You retain ownership of your data. If you terminate your participation, you may request export or deletion of your data."
    ]
  },
  {
    heading: "9. FEEDBACK AND BUG REPORTING",
    body: [
      "As an early access Participant, you agree to:",
      {
        bullets: [
          "Report bugs, errors, or unexpected behavior when you encounter them",
          "Provide reasonable feedback on your experience using the platform",
          "Not publicly disclose non-public features or defects without our prior consent"
        ]
      },
      "Any feedback, suggestions, or ideas you provide may be used by LienGuardian without restriction or compensation."
    ]
  },
  {
    heading: "10. TERM AND TERMINATION",
    body: [
      "This Agreement is effective upon your acceptance and continues until terminated. Either party may terminate at any time for any reason by providing written notice (email is sufficient).",
      "Upon termination:",
      {
        bullets: [
          "Your access to the platform will be revoked",
          "Pending certified mailings that have already been dispatched will not be recalled",
          "Sections 2, 5, 6, 7, and 11 survive termination"
        ]
      }
    ]
  },
  {
    heading: "11. GENERAL PROVISIONS",
    body: [
      {
        bullets: [
          "Governing Law: This Agreement is governed by the laws of the State of Florida, without regard to conflict of law principles.",
          "Dispute Resolution: Any disputes arising under this Agreement shall be resolved in the state or federal courts located in Palm Beach County, Florida.",
          "Entire Agreement: This Agreement constitutes the entire agreement between the parties regarding the subject matter herein and supersedes all prior agreements and understandings.",
          "Severability: If any provision of this Agreement is found to be unenforceable, the remaining provisions shall continue in full force and effect.",
          "No Waiver: Our failure to enforce any right or provision of this Agreement shall not constitute a waiver of that right or provision.",
          "Assignment: You may not assign this Agreement without our prior written consent. We may assign this Agreement at any time."
        ]
      }
    ]
  },
  {
    heading: "ACCEPTANCE",
    body: [
      "By clicking \"I Accept\" below, you confirm that:",
      {
        bullets: [
          "You have read and understood this Agreement in its entirety",
          "You are authorized to enter into this Agreement on behalf of your company",
          "You understand that LienGuardian is in early access and may contain errors",
          "You accept full responsibility for verifying all documents and data before relying on them",
          "You understand that certified mail sent through the platform is irreversible and incurs real charges"
        ]
      }
    ]
  }
];

/**
 * True once the reader has reached the bottom of the agreement pane. Split out
 * of the component so the rule is testable without a layout engine — jsdom
 * reports every scroll dimension as 0.
 */
export function isScrolledToEnd(
  box: { scrollTop: number; scrollHeight: number; clientHeight: number },
  slackPx = 24,
): boolean {
  return box.scrollHeight - box.scrollTop - box.clientHeight <= slackPx;
}
